import {
  DEFAULT_PERIOD_LENGTH,
  LANE_HEIGHT,
  NOTE_SIZE,
  canEnter,
  childrenOf,
  createCtx,
  displayOf,
  edgeLabel,
  effectiveTimeline,
  flagText,
  formatSceneTime,
  formatStamp as formatTime,
  fuzzySpan,
  isDocked,
  isNoteType,
  laneLayout,
  nodeTimes,
  posAtBasis,
  inferredTimes,
  posAtTime,
  timeAt,
  timeDisplayOf,
  timelineWarps,
  visibleOn,
  worldSpanOf,
  momentOf,
  momentText,
  type SceneTime,
  type StoryCtx,
  type StoryNode,
  type TimelineData,
  type TimeWarp,
} from '@dispositio/core';
import type { Story } from '@dispositio/core';
import { kidsOf, type Kid } from './contentLayout';
import { BADGE, previewLayout, previewShowOf, previewTitleLayout, tint, type PreviewShow, type PreviewSpec } from './previewCard';

/** Grobe Zoombereiche für die Anzeige (Fern, Mittel, Nah); die Darstellung selbst ist auf jeder Stufe dieselbe. */
export type Lod = 0 | 1 | 2;
export const LOD_THRESHOLDS = { box: 0.45, preview: 1.3 };
export const lodFor = (zoom: number): Lod => (zoom < LOD_THRESHOLDS.box ? 0 : zoom < LOD_THRESHOLDS.preview ? 1 : 2);

/** Höhe der Zeitstrahl-Elemente im Modellraum */
export const AXIS_HEIGHT = 16;
/** Höhe von Zeitraum und Zeitpunkt auf dem Zeitstrahl (runde Marke, siehe previewCard) */
export const PERIOD_HEIGHT = BADGE;
/** Breite des Zeitpunkt-Symbols (zwei Dreiecke, Spitze an Spitze); hoch wie ein Zeitraum */
export const EVENT_SIZE = BADGE;
/** Höhe des unscharfen Bereichs einer ungenauen Zeit (AP-19): ein Band auf der Achse, flacher als die Marke */
export const BLUR_HEIGHT = Math.round(BADGE * 0.72);
/** Abstand der Spuren eines Zeitstrahls: überlappende Zeiträume und Ereignisse liegen auf eigenen Schienen */
export { LANE_HEIGHT };
/** Zieht man ein angedocktes Element weiter als so weit von seinem Zeitstrahl weg, löst es sich beim Loslassen vom Zeitstrahl */
export const UNDOCK_DISTANCE = 130;
/** Abstand eines neuen Zeitfähnchens über dem Zeitstrahl (y), solange der Nutzer es nicht verschoben hat */
export const DEFAULT_FLAG_DY = -64;
/** Standardabstand der Beschriftungsbox unter einem Ereignis bzw. Zeitraum (y, ab Mitte des Elements) */
/** Abstand zwischen Marke und Vorschaukarte (Länge der gestrichelten Linie) */
export const PREVIEW_GAP = 44;

export type Role = 'card' | 'event' | 'period' | 'axis' | 'flag' | 'flagpin' | 'label' | 'handle' | 'note' | 'grip' | 'notepin' | 'blur';

/** Ein Element der Zeichenfläche (Cytoscape-Knoten). Die Größe einer Karte ergibt sich aus ihrem Inhalt, siehe cardSvg. */
export interface VNode {
  /** Cytoscape-ID: Node-ID, bei Fähnchen, Griffen und weiteren Spuren eine abgeleitete ID */
  id: string;
  /** Zahl der Widersprüche (PF 54): roter Punkt an Marke bzw. Karte */
  issues?: number;
  role: Role;
  /** Der Story-Node, für den das Element steht (bei Fähnchen/Achse der Zeitstrahl) */
  nodeId: string;
  x: number;
  y: number;
  /** Größe im Modellraum für Zeitstrahl-Elemente */
  w: number;
  h: number;
  title: string;
  hover: string;
  /** Zeit (Zeitpunkt bzw. Zeitraum oder die Zeit am Rand eines Zeitraums), für die Hover-Anzeige */
  when: string;
  typeName: string;
  typeColor: string;
  tagColor: string | null;
  /** Szene im Kapitel: die blasse Farbe des Kapitels (Füllung der Vorschaukarte; Rand und Marke dunkler, `ownColor`) */
  chapterColor: string | null;
  imageId: string | null;
  /** Verweisschlüssel des Eintrags (Instanzen) */
  refKey: string | null;
  empty: boolean;
  /** Inhalt des Nodes für die Vorschau (nach Klasse gruppiert) */
  kids: Kid[];
  /** Doppelklick öffnet den inneren Canvas */
  enterable: boolean;
  /** Wie sich das Element ziehen lässt */
  drag: 'free' | 'none';
  /**
   * Fähnchen: id und Art; Griff: Seite des Zeitraums; angedockt: Zeitstrahl und y-Lage seiner Spur
   * (daran bleibt das Element beim Ziehen hängen, bis es sich löst).
   */
  meta?: {
    flagId?: string;
    flagKind?: 'start' | 'end' | 'user';
    edge?: 'from' | 'to';
    pos?: number;
    labelDy?: number;
    /** Vorschaukarte mit Fähnchen: der Status der Szene (Knopf in der Erzähllinie) */
    status?: string;
    /** Vorschaukarte nur als Titel (reduzierte Vorschau, siehe titleOnlyPreviews) */
    titleOnly?: boolean;
    show?: PreviewShow;
    docked?: { timelineId: string; y: number };
    /** Name einer Zeitlinie (erste Schiene): Versatz zur Mitte ihrer Schienen, dort steht er links vom Start (Modellraum) */
    nameDy?: number;
    /** Notizzettel: sein Text */
    text?: string;
    /** Notizzettel mit Zeit (PF 14): die Zeitlinie, an der er hängt, und die Höhe ihrer Achse */
    dated?: { timelineId: string; axisY: number };
    /** Hintergrundereignis (PF 15): Szene, die bewusst nicht erzählt wird – schlicht gezeichnet */
    background?: boolean;
    /** erschlossene Zeit (PF 52): blasser Bereich einer Szene ohne Zeit zwischen ihren Nachbarn */
    inferred?: boolean;
  };
}

/** Kantenlänge des Griffs, mit dem man die Größe eines gewählten Notizzettels ändert */
export const GRIP_SIZE = 16;

/**
 * Zeit eines Notizzettels an der Stelle `pos` einer Zeitlinie (PF 14): Beginn dort, ein Zeitraum behält seine Dauer, die Genauigkeit
 * bleibt; null ohne Zeitrechnung.
 */
export function noteTimeAt(note: StoryNode, t: TimelineData, pos: number): SceneTime | null {
  const start = timeAt(t, pos);
  if (!start) return null;
  const [a, b, s] = [momentOf(note.time?.start), momentOf(note.time?.end), momentOf(start)];
  const end = a && b && s && a.basis === b.basis && s.basis === a.basis ? momentText({ ...s, value: s.value + (b.value - a.value) }) : null;
  return { start, end, ...(note.time?.precision ? { precision: note.time.precision } : {}), source: 'timeline' };
}

/** Wo ein Notizzettel mit Zeit an einer Zeitlinie hängt: Zeitlinie und gezeigte Stelle von Beginn und Ende */
export interface NotePin {
  tl: StoryNode;
  x0: number;
  x1: number;
}

/**
 * Notizzettel: eigene Größe (sonst die Standardgröße), Farbe als eigene Farbe (sonst die der Klasse), der Text; gewählt dazu der Griff.
 * Mit Zeit (PF 14) steht er mittig über seiner Stelle auf der Zeitlinie (seine Höhe bleibt frei); eine gestrichelte Linie führt zu einer
 * Marke auf der Achse, mit Ende ein schmaler Balken über die Spanne.
 */
function noteParts(ctx: StoryCtx, n: StoryNode, picked: boolean, pin: NotePin | null): VNode[] {
  const w = n.w ?? NOTE_SIZE.w;
  const h = n.h ?? NOTE_SIZE.h;
  const axisY = pin ? laneY(pin.tl, 0) : 0;
  const note = base(ctx, n, {
    role: 'note',
    w,
    h,
    kids: [],
    enterable: false,
    ...(pin ? { x: pin.x0 } : {}),
    meta: { text: String(n.fields.text ?? ''), ...(pin ? { dated: { timelineId: pin.tl.id, axisY } } : {}) },
  });
  const out = [note];
  if (pin) out.push({ ...note, id: `notepin:${n.id}`, role: 'notepin', x: (pin.x0 + pin.x1) / 2, y: axisY, w: Math.max(pin.x1 - pin.x0, 10), h: 10, drag: 'none', meta: { dated: note.meta!.dated } });
  if (picked) out.push({ ...note, id: `grip:${n.id}`, role: 'grip', x: note.x + w / 2, y: note.y + h / 2, w: GRIP_SIZE, h: GRIP_SIZE, meta: {} });
  return out;
}

export interface VEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  /** Verbindungslinie eines Zeitfähnchens bzw. einer Vorschaukarte zu ihrem Platz auf dem Zeitstrahl (keine Story-Verbindung) */
  flagLine?: boolean;
  /** Farbe der Linie (Vorschaukarte: Farbe des Elements, gestrichelt) */
  color?: string;
}

export interface CanvasModel {
  nodes: VNode[];
  edges: VEdge[];
  /** Zeitraffer je Zeitlinie (PF 10): lineare Lage der Fläche → gezeigte; fehlt einer, ist die Achse linear */
  warps?: ReadonlyMap<string, TimeWarp>;
}

const laneOf = (tl: StoryNode): { x: number; y: number } => ({ x: tl.x ?? 0, y: tl.y ?? 0 });

/** y-Lage der Spur `lane` (0 = oberste) eines Zeitstrahls. */
export const laneY = (tl: StoryNode, lane: number): number => laneOf(tl).y + lane * LANE_HEIGHT;

/** Achsenwert (gespeicherte Stelle) für eine Canvas-X-Koordinate auf diesem Zeitstrahl; mit Zeitraffer über seine Umkehrung. */
export const axisValueAt = (tl: StoryNode, x: number, warp?: TimeWarp): number => Math.round((warp ? warp.toLinear(x) : x) - laneOf(tl).x);

/** Canvas-X-Koordinate einer Stelle des Zeitstrahls; mit Zeitraffer dort, wo er sie zeigt. */
export const axisX = (tl: StoryNode, pos: number, warp?: TimeWarp): number => (warp ? warp.toDisplay(laneOf(tl).x + pos) : laneOf(tl).x + pos);

/** „2024-05-01T08:00“ → „01.05.2024 08:00“ (Beschriftungen und Zeitanzeigen), auf die Teile der Zeitanzeige des Zeitstrahls begrenzt. Anderer Text bleibt unverändert. */
export const formatStamp = formatTime;

/** Zeitanzeige des Zeitstrahls, an dem ein Node angedockt ist (sonst alles anzeigen). */
const displayFor = (ctx: StoryCtx, n: StoryNode) => timeDisplayOf(n.parentId ? ctx.nodes.get(n.parentId)?.timeline : undefined);

/** Zeit an einer bzw. zwischen zwei Stellen eines Zeitstrahls („12.04.“ bzw. „Tag 3 – Tag 5“), leer ohne Zeitrechnung. Für die Live-Anzeige beim Ziehen. */
export function whenBetween(ctx: StoryCtx, tl: StoryNode, a: number, b?: number): string {
  const t = effectiveTimeline(ctx, tl);
  if (!t) return '';
  const display = timeDisplayOf(t);
  const fa = timeAt(t, a);
  const fb = b === undefined ? null : timeAt(t, b);
  const sa = fa ? formatStamp(fa, display) : '';
  const sb = fb ? formatStamp(fb, display) : '';
  return sa && sb ? `${sa} – ${sb}` : sa || sb;
}

/**
 * Zeitanzeige eines Ereignisses („01.05.2024 12:00“) bzw. Zeitraums („01.05.2024 06:00 – 01.05.2024 12:00“), begrenzt auf die Genauigkeit
 * der Zeit („03.1823“, „ca. 1823“); leer, wenn unbekannt.
 */
export function whenText(ctx: StoryCtx, n: StoryNode): string {
  if (n.kind !== 'event' && n.kind !== 'period') return '';
  const t = nodeTimes(ctx, n);
  const display = displayFor(ctx, n);
  const a = formatSceneTime(t.start, n.time, display);
  const b = formatSceneTime(t.end, n.time, display);
  return a && b ? `${a} – ${b}` : a || b;
}

/** Zeit an einem Rand eines angedockten Zeitraums (leer, wenn der Zeitstrahl keine Datumsangaben hat). */
const edgeTime = (ctx: StoryCtx, n: StoryNode, edge: 'from' | 'to'): string => {
  const t = nodeTimes(ctx, n);
  const iso = edge === 'from' ? t.start : t.end;
  return iso ? formatSceneTime(iso, n.time, displayFor(ctx, n)) : `Position ${n.kind === 'event' ? n.at : edge === 'from' ? n.from : n.to}`;
};

/**
 * Wohin ein Ereignis bzw. Zeitraum am Zeitstrahl `tl` andockt, wenn es bei `dropX` (Canvas-X) losgelassen wird: Hat der Node eine Zeit,
 * die sich auf den Zeitstrahl übertragen lässt, an der Stelle dieser Zeit – über die gemeinsame Vergleichsbasis (z. B. von einem
 * Zeitstrahl mit eigener Zählung und Nullpunkt auf einen datierten) oder über den Text der Zeit („Tag 5“ in der Zählung des Ziels) –,
 * sonst an der Stelle des Loslassens (ein Zeitraum dort mittig, mit der Standardlänge).
 */
export function dockPlacement(ctx: StoryCtx, node: StoryNode, tl: StoryNode, dropX: number, warp?: TimeWarp): { at: number } | { from: number; to: number } {
  const t = effectiveTimeline(ctx, tl);
  const times = nodeTimes(ctx, node);
  const w = worldSpanOf(ctx, node);
  const place = (text: string | null, value: number | undefined): number | null => {
    if (!t || !text) return null;
    return (w && value !== undefined ? posAtBasis(t, w.basis, value) : null) ?? posAtTime(t, text);
  };
  const startPos = place(times.start, w?.start);
  if (node.kind === 'event') return { at: Math.round(startPos ?? axisValueAt(tl, dropX, warp)) };
  const endPos = place(times.end, w?.end);
  if (startPos !== null && endPos !== null && endPos > startPos) return { from: startPos, to: endPos };
  const from = Math.round(startPos ?? axisValueAt(tl, dropX, warp) - DEFAULT_PERIOD_LENGTH / 2);
  return { from, to: from + DEFAULT_PERIOD_LENGTH };
}

function base(ctx: StoryCtx, n: StoryNode, over: Partial<VNode>): VNode {
  const d = displayOf(ctx, n);
  const background = !!n.narrateSkip && (n.kind === 'event' || n.kind === 'period');
  return {
    id: n.id,
    role: 'card',
    nodeId: n.id,
    x: n.x ?? 0,
    y: n.y ?? 0,
    w: 0,
    h: 0,
    title: d.title,
    hover: d.hover,
    when: whenText(ctx, n),
    typeName: d.typeName,
    typeColor: d.typeColor,
    tagColor: d.tagColor,
    chapterColor: d.chapterColor,
    imageId: d.imageId,
    refKey: d.refKey,
    empty: d.empty,
    kids: kidsOf(ctx, n),
    enterable: canEnter(n),
    drag: 'free',
    ...over,
    ...(background ? { meta: { ...over.meta, background } } : {}),
  };
}

const flagNode = (tl: StoryNode, key: string, pos: number, label: string, kind: 'start' | 'end' | 'user', flagId: string, color: string, x: number, y: number, h: number, baseY: number): VNode => ({
  id: `flag:${tl.id}:${key}`,
  role: 'flag',
  nodeId: tl.id,
  x,
  y,
  w: 3,
  h,
  title: label,
  hover: '',
  when: '',
  typeName: '',
  typeColor: color,
  tagColor: null,
  chapterColor: null,
  imageId: null,
  refKey: null,
  empty: false,
  kids: [],
  enterable: false,
  drag: 'free',
  meta: { flagId, flagKind: kind, pos, docked: { timelineId: tl.id, y: baseY } },
});

/** Inhalt der Vorschaukarte eines Zeitpunkts bzw. Zeitraums (aus den Anzeigedaten des Nodes). */
/**
 * Farbe von Rand und Marke eines Elements: die eigene, sonst bei Szenen im Kapitel dessen Farbe dunkler (die blasse Kapitelfarbe
 * wäre als Linie und Schrift kaum zu sehen), sonst die der Klasse.
 */
export const ownColor = (v: Pick<VNode, 'tagColor' | 'chapterColor' | 'typeColor'>): string => v.tagColor ?? (v.chapterColor ? tint(v.chapterColor, '#000000', 0.42) : v.typeColor);

export const previewSpecOf = (v: Pick<VNode, 'title' | 'when' | 'hover' | 'kids' | 'tagColor' | 'chapterColor' | 'typeColor' | 'meta'>, show: PreviewShow): PreviewSpec => ({
  title: v.title,
  when: v.when,
  description: v.hover,
  kids: v.kids,
  color: ownColor(v),
  ...(!v.tagColor && v.chapterColor ? { fill: v.chapterColor } : {}),
  ...(v.meta?.status ? { status: v.meta.status } : {}),
  show,
});

/** Was das Modell außer dem Story-Inhalt zeigt */
export interface CanvasOptions {
  /** Vorschaukarten mit dem Status der Szene als Fähnchen (Knopf in der Erzähllinie) */
  status?: boolean;
  /** Zeitraffer (PF 10): Zeitlinien zeigen die Zeit berechnet statt proportional (Schalter über der Fläche) */
  raffer?: boolean;
  /** Widersprüche je Node (PF 54): ihre Marke bzw. Karte trägt einen roten Punkt mit der Zahl */
  issues?: ReadonlyMap<string, number>;
}

/**
 * Vorschaukarte eines angedockten Zeitpunkts bzw. Zeitraums: steht über der Marke (auf weiteren Schienen darunter), mit
 * gestrichelter Linie zur Marke, und lässt sich senkrecht verschieben. Mit `opts.status` trägt sie oben rechts den Status der Szene.
 */
function previewPart(ctx: StoryCtx, n: StoryNode, x: number, y: number, lane: number, timelineId: string, opts: CanvasOptions): VNode {
  if (n.narrateSkip) {
    // Hintergrundereignis (PF 15): statt der Vorschaukarte nur der Titel, nah an der Marke
    const v = base(ctx, n, { x, y });
    const t = previewTitleLayout(v.title);
    const away = BADGE / 2 + PREVIEW_GAP / 2 + t.h / 2;
    const dy = n.labelDy ?? (lane === 0 ? -away : away);
    return { ...v, id: `label:${n.id}`, role: 'label', y: y + dy, w: t.w, h: t.h, drag: 'free', enterable: false, meta: { labelDy: dy, titleOnly: true, background: true, docked: { timelineId, y } } };
  }
  const show = previewShowOf(n.labelShow);
  const status = opts.status && typeof n.fields.status === 'string' ? n.fields.status.trim() : '';
  const v = base(ctx, n, { x, y });
  const layout = previewLayout(previewSpecOf({ ...v, meta: { status } }, show));
  const away = BADGE / 2 + PREVIEW_GAP + layout.h / 2;
  const dy = n.labelDy ?? (lane === 0 ? -away : away);
  return { ...v, id: `label:${n.id}`, role: 'label', y: y + dy, w: layout.w, h: layout.h, drag: 'free', enterable: false, meta: { labelDy: dy, show, docked: { timelineId, y }, ...(status ? { status } : {}) } };
}

/**
 * Reduzierte Vorschau (Knopf in der Erzähllinie): die Vorschaukarten aller Szenen außer `keep` nur noch als Titel. Er steht an der
 * Kante der Karte, die zur Marke zeigt (die gestrichelte Linie bleibt gleich lang), und lässt sich nicht verschieben.
 */
export function titleOnlyPreviews(model: CanvasModel, keep: ReadonlySet<string>): CanvasModel {
  const nodes = model.nodes.map((v): VNode => {
    if (v.role !== 'label' || keep.has(v.nodeId) || v.meta?.titleOnly) return v;
    const t = previewTitleLayout(v.title);
    const dy = v.meta?.labelDy ?? 0;
    const near = dy < 0 ? dy + v.h / 2 - t.h / 2 : dy - v.h / 2 + t.h / 2;
    return { ...v, y: v.y - dy + near, w: t.w, h: t.h, drag: 'none', meta: { ...v.meta, labelDy: near, titleOnly: true } };
  });
  return { ...model, nodes };
}

/**
 * Alles, was auf dem Canvas des Nodes `currentId` zu sehen ist: Karten (auch lose Ereignisse und Zeiträume), Zeitstrahle samt
 * angedockter Ereignisse/Zeiträume (auf eigenen Spuren, wenn sie sich überlappen) und Fähnchen, und die sichtbaren Verbindungen
 * zwischen den gezeigten Nodes. Unsichtbare Verbindungen und die Verknüpfungen zu den Foundationals werden nicht gezeichnet.
 */
export function buildCanvasModel(story: Story | StoryCtx, currentId: string, selectedId: string | null = null, opts: CanvasOptions = {}): CanvasModel {
  const ctx: StoryCtx = 'nodes' in story && story.nodes instanceof Map ? (story as StoryCtx) : createCtx(story as Story);
  const out: VNode[] = [];
  const shown = new Set<string>();
  const periods: VNode[] = [];
  const events: VNode[] = [];
  const handles: VNode[] = [];
  const flagLines: VEdge[] = [];
  const labels: VNode[] = [];
  // mehrere Zeitlinien: jede trägt ihren Namen
  const several = childrenOf(ctx, ctx.story.rootId).filter((n) => n.kind === 'timeline').length > 1;
  // Zeitraffer: wo jede Stelle eines Zeitstrahls gezeigt wird; die Spuren zählen die Überschneidung dort
  const warps: ReadonlyMap<string, TimeWarp> = opts.raffer ? timelineWarps(ctx) : new Map();
  const shownAt = (tl: StoryNode, pos: number): number => axisX(tl, pos, warps.get(tl.id));
  // Notizzettel mit Zeit (PF 14): an der ersten Zeitlinie (die Hauptzeitlinie zuerst), deren Spanne die Zeit enthält, sonst an der
  // Hauptzeitlinie; bildet keine die Zeit ab, liegt er frei
  const timelines = childrenOf(ctx, ctx.story.rootId).filter((n) => n.kind === 'timeline' && n.timeline);
  const pinOf = (n: StoryNode): NotePin | null => {
    const start = n.time?.start;
    if (!start) return null;
    const at = (tl: StoryNode, v: string): number | null => posAtTime(tl.timeline!, v);
    const inside = timelines.find((tl) => {
      const p = at(tl, start);
      return p !== null && p >= tl.timeline!.startPos && p <= tl.timeline!.endPos;
    });
    const tl = inside ?? (timelines[0] && at(timelines[0], start) !== null ? timelines[0] : undefined);
    if (!tl) return null;
    const p0 = at(tl, start)!;
    const p1 = n.time?.end ? (at(tl, n.time.end) ?? p0) : p0;
    return { tl, x0: shownAt(tl, p0), x1: shownAt(tl, Math.max(p0, p1)) };
  };
  /**
   * Ungenaue Zeit (AP-19, PF 58): ein blasser Bereich über die Spanne, die ihre Genauigkeit offenlässt („im März 1823“ den ganzen März),
   * unter Marke bzw. Balken; nur, wenn er über das Element hinausreicht. `x0`/`x1`: gezeigte Ausdehnung des Elements.
   */
  const blurOf = (n: StoryNode, tl: StoryNode, y: number, x0: number, x1: number): VNode | null => {
    const span = fuzzySpan(n.time);
    const t = span ? effectiveTimeline(ctx, tl) : null;
    if (!span || !t) return null;
    const a = posAtBasis(t, span.basis, span.start);
    const b = posAtBasis(t, span.basis, span.end);
    if (a === null || b === null || !Number.isFinite(a) || !Number.isFinite(b)) return null;
    const clamp = (p: number): number => Math.min(Math.max(p, t.startPos), t.endPos);
    const [from, to] = [shownAt(tl, clamp(a)), shownAt(tl, clamp(b))];
    if (to - from < 1 || (from >= x0 - 3 && to <= x1 + 3)) return null;
    return { ...base(ctx, n, { role: 'blur', x: (from + to) / 2, y, w: to - from, h: BLUR_HEIGHT, drag: 'none', enterable: false, kids: [], meta: { docked: { timelineId: tl.id, y } } }), id: `blur:${n.id}` };
  };
  const blurs: VNode[] = [];
  const lanesCache = new Map<string, ReturnType<typeof laneLayout>>();
  const lanesOf = (tl: StoryNode): ReturnType<typeof laneLayout> => {
    let l = lanesCache.get(tl.id);
    if (!l) lanesCache.set(tl.id, (l = laneLayout(ctx, tl.id, warps.has(tl.id) ? (p) => shownAt(tl, p) : undefined)));
    return l;
  };

  for (const n of visibleOn(ctx, currentId)) {
    shown.add(n.id);
    switch (n.kind) {
      case 'timeline': {
        // innere Zeitleiste eines Zeitraums: Start und Ende tragen dessen Beginn und Ende
        const t = effectiveTimeline(ctx, n);
        if (!t) break;
        const lane = laneOf(n);
        const d = displayOf(ctx, n);
        const lanes = lanesOf(n);
        const span = (lanes.count - 1) * LANE_HEIGHT;
        const [x0, x1] = [shownAt(n, t.startPos), shownAt(n, t.endPos)];
        // Name links vom Start (die Zeiten stehen unter Start- und Endmarke), nur bei mehreren Zeitlinien – sonst genügen die Zeiten
        const name = several ? d.title : '';
        for (let i = 0; i < lanes.count; i++) {
          out.push(
            base(ctx, n, {
              // die erste Schiene trägt die ID des Zeitstrahls, weitere abgeleitete
              id: i === 0 ? n.id : `axis:${n.id}:${i}`,
              role: 'axis',
              x: (x0 + x1) / 2,
              y: laneY(n, i),
              w: x1 - x0,
              h: AXIS_HEIGHT,
              title: i === 0 ? name : '',
              when: '',
              enterable: false,
              // an der Achse gegriffen zieht die ganze Zeitlinie mit (siehe StoryCanvas)
              drag: 'free',
              meta: { docked: { timelineId: n.id, y: laneY(n, i) }, ...(i === 0 ? { nameDy: span / 2 } : {}) },
            }),
          );
        }
        const mid = lane.y + span / 2;
        out.push(flagNode(n, 'start', t.startPos, edgeLabel(t, 'start'), 'start', 'start', d.typeColor, x0, mid, 64 + span, mid));
        out.push(flagNode(n, 'end', t.endPos, edgeLabel(t, 'end'), 'end', 'end', d.typeColor, x1, mid, 64 + span, mid));
        // Zeitfähnchen: die Box mit der Beschriftung liegt `dy` über bzw. unter dem Zeitstrahl, eine Linie führt zu ihrer Stelle auf dem Zeitstrahl
        for (const f of t.flags) {
          const x = shownAt(n, f.pos);
          const y = lane.y + (f.dy ?? DEFAULT_FLAG_DY);
          out.push(flagNode(n, f.id, f.pos, flagText(t, f), 'user', f.id, d.typeColor, x, y, 24, lane.y));
          const pinY = Math.min(Math.max(y, lane.y), lane.y + span);
          out.push({ ...flagNode(n, f.id, f.pos, '', 'user', f.id, d.typeColor, x, pinY, 8, lane.y), id: `flagpin:${n.id}:${f.id}`, role: 'flagpin', w: 8, drag: 'none' });
          flagLines.push({ id: `flagline:${n.id}:${f.id}`, source: `flag:${n.id}:${f.id}`, target: `flagpin:${n.id}:${f.id}`, label: '', flagLine: true });
        }
        break;
      }
      case 'event': {
        if (!isDocked(ctx, n)) {
          out.push(base(ctx, n, {}));
          break;
        }
        const tl = ctx.nodes.get(n.parentId!)!;
        const lane = lanesOf(tl).lane.get(n.id) ?? 0;
        const y = laneY(tl, lane);
        const ex = shownAt(tl, n.at ?? 0);
        events.push(base(ctx, n, { role: 'event', x: ex, y, w: EVENT_SIZE, h: PERIOD_HEIGHT, drag: 'free', kids: [], meta: { docked: { timelineId: tl.id, y } } }));
        const blur = blurOf(n, tl, y, ex - EVENT_SIZE / 2, ex + EVENT_SIZE / 2);
        if (blur) blurs.push(blur);
        labels.push(previewPart(ctx, n, ex, y, lane, tl.id, opts));
        break;
      }
      case 'period': {
        if (!isDocked(ctx, n)) {
          out.push(base(ctx, n, {}));
          break;
        }
        const tl = ctx.nodes.get(n.parentId!)!;
        const lane = lanesOf(tl).lane.get(n.id) ?? 0;
        const y = laneY(tl, lane);
        const from = shownAt(tl, n.from ?? 0);
        const to = shownAt(tl, n.to ?? n.from ?? 0);
        const w = Math.max(to - from, 1);
        const x = (from + to) / 2;
        periods.push(base(ctx, n, { role: 'period', x, y, w, h: PERIOD_HEIGHT, drag: 'free', kids: [], meta: { docked: { timelineId: tl.id, y } } }));
        const blur = blurOf(n, tl, y, from, to);
        if (blur) blurs.push(blur);
        labels.push(previewPart(ctx, n, x, y, lane, tl.id, opts));
        // Die Ränder zeigen bei Hover ihre Zeit; der Zeitraum zeigt Griffe zum Verändern, sobald er ausgewählt ist
        const picked = selectedId === n.id;
        for (const edge of ['from', 'to'] as const) {
          handles.push({
            ...base(ctx, n, {
              role: 'handle',
              x: x + (edge === 'from' ? -w / 2 : w / 2),
              y,
              w: picked ? 10 : 8,
              h: picked ? 30 : 22,
              when: edgeTime(ctx, n, edge),
              drag: picked ? 'free' : 'none',
              enterable: false,
              kids: [],
              meta: { edge, docked: { timelineId: tl.id, y } },
            }),
            id: `handle:${n.id}:${edge}`,
          });
        }
        break;
      }
      default:
        if (isNoteType(ctx.types.get(n.typeId))) out.push(...noteParts(ctx, n, selectedId === n.id, pinOf(n)));
        else out.push(base(ctx, n, {}));
    }
  }
  // erschlossene Zeit (PF 52): Szenen ohne Zeit als blasser Bereich zwischen ihren Nachbarn in Lesereihenfolge, an der Hauptzeitlinie
  const main = timelines[0];
  const mainT = main && main.parentId === currentId ? effectiveTimeline(ctx, main) : null;
  if (main && mainT) {
    for (const [id, inf] of inferredTimes(ctx)) {
      const n = ctx.nodes.get(id);
      const a = posAtBasis(mainT, inf.basis, inf.start);
      const b = posAtBasis(mainT, inf.basis, inf.end);
      if (!n || shown.has(id) || a === null || b === null || !Number.isFinite(a) || !Number.isFinite(b)) continue;
      const clamp = (p: number): number => Math.min(Math.max(p, mainT.startPos), mainT.endPos);
      const [from, to] = [shownAt(main, clamp(a)), shownAt(main, clamp(b))];
      if (to - from < 1) continue;
      const y = laneY(main, 0);
      blurs.push({ ...base(ctx, n, { role: 'blur', x: (from + to) / 2, y, w: to - from, h: BLUR_HEIGHT, drag: 'none', enterable: false, kids: [], meta: { docked: { timelineId: main.id, y }, inferred: true } }), id: `infer:${id}` });
    }
  }
  // Zeichenreihenfolge: Achsen/Fähnchen, unscharfe Bereiche, Zeiträume, Karten, Notizzettel, Ereignisse, Griffe
  const axes = out.filter((v) => v.role === 'axis' || v.role === 'flag' || v.role === 'flagpin' || v.role === 'notepin');
  const cards = out.filter((v) => v.role === 'card');
  const notes = out.filter((v) => v.role === 'note');
  const grips = out.filter((v) => v.role === 'grip');
  // Widersprüche (PF 54): an der Marke einer angedockten Szene bzw. an der Karte einer losen
  const marked = (list: VNode[]): VNode[] => (opts.issues?.size ? list.map((v) => (opts.issues!.get(v.nodeId) ? { ...v, issues: opts.issues!.get(v.nodeId) } : v)) : list);
  const nodes = [...axes, ...blurs, ...marked(periods), ...marked(cards), ...notes, ...marked(events), ...handles, ...grips, ...labels];
  // gestrichelte Linie von der Vorschaukarte zur Marke
  for (const v of labels) flagLines.push({ id: `labelline:${v.nodeId}`, source: v.id, target: v.nodeId, label: '', flagLine: true, color: ownColor(v) });
  // gestrichelte Linie vom Zettel mit Zeit zu seiner Stelle auf der Achse
  for (const v of out) if (v.role === 'notepin') flagLines.push({ id: `noteline:${v.nodeId}`, source: v.nodeId, target: v.id, label: '', flagLine: true, color: ownColor(v) });

  const edges: VEdge[] = ctx.story.connections
    .filter((c) => c.visible && shown.has(c.sourceId) && shown.has(c.targetId))
    .map((c) => ({ id: c.id, source: c.sourceId, target: c.targetId, label: c.label }));
  return { nodes, edges: [...flagLines, ...edges], warps };
}

/** Anzahl der Nodes direkt im Node `id` (für Anzeigen wie „3 Nodes darin“). */
export const innerCount = (ctx: StoryCtx, id: string): number => childrenOf(ctx, id).filter((n) => n.kind !== 'timeline').length;
